import React, { useState, useCallback, useEffect } from "react";
import { useLoaderData, useRevalidator } from "react-router";
import {
  Tab,
  Tabs,
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
} from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import { useTranslation } from "react-i18next";
import {
  ClientInfoCard,
  RiskAllergyCard,
  AppointmentHistoryCard,
  NextAppointmentCard,
  NextAppointmentNoteCard,
} from "../components/client/cards";
import ClientAvatar from "../components/client/ClientAvatar";
import { NewAdhocDialog } from "../components/reservationDialog/NewAdhocDialog";
import {
  RESERVATION_MODES,
  ReservationDialog,
} from "../components/reservationDialog/ReservationDialog";
import * as api from "../services/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/free-solid-svg-icons";
import { useRoles } from "../context/AuthenticationTokenContext";
import { role, isGrantedAny } from "shared/src/roles.mjs";
import { currentLocationFromRequest } from "../components/LocationSelector";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import History from "../components/client/cards/History";
import EditDialog from "../components/client/EditDialog";
import { startOfDay } from "shared/src/date.mjs";
import * as d from "shared/src/date.mjs";
import { Gallery } from "../components/client/Gallery";
import { PrefCard } from "../components/client/cards/PrefCard";
import { ContactsCard } from "../components/client/cards/ContactsCard";
import { appointmentAdhocType, status } from "shared/src/appointment.mjs";
import { TreatmentCard } from "../components/client/cards/TreatmentCard";
import { PaymentCard } from "../components/client/cards/PaymentCard";
import { Documents } from "../components/client/Documents";
import { canBeTreated } from "shared/src/client.mjs";
import { formData, FormErrorList, useFormAction } from "../form";
import ButtonLoader from "../components/ButtonLoader";

export async function loader({ request, params }) {
  const location_id = currentLocationFromRequest(request);

  const now = new Date();
  const today = startOfDay(now);

  const [clientDataRes, appointmentsRes, activeNotesRes, reservationSettings] =
    await Promise.all([
      api.loadClientFullData(
        {
          now: d.toString(now),
          id: params.id,
          nameDaysFilter: {
            month: { eq: today.getMonth() + 1 },
            day: { eq: today.getDate() },
          },
          lastAppointmentFilter: {
            client_id: { eq: params.id },
            status_id: { in: [status.TREATED, status.FINISHED] },
          },
        },
        api.contextFromRequest(request),
      ),
      api.loadAppointments({
        filter: {
          client_id: { eq: params.id },
          start: { gte: d.toString(today) },
        },
        location_id,
      }),
      api.nextAppointmentNotes({
        client_id: { eq: params.id },
        active: { eq: true },
      }),
      api.loadReservationLocationSettingsData({
        locationSettingsFilter: {
          location_id: { eq: location_id },
          key: { eq: "reservation_settings" },
        },
      }),
    ]);

  const nextAppointmentNotes =
    activeNotesRes?.data?.nextAppointmentNotes?.data ?? [];

  if (!clientDataRes.data?.client) {
    throw new Response("Not Found", { status: 404 });
  }

  clientDataRes.data.client.contacts = clientDataRes.data.client.contacts.sort(
    (a, b) => a.created_at?.localeCompare(b.created_at),
  );
  return {
    nameDayNames: new Set(clientDataRes.data.nameDays.data.map((d) => d.name)),
    client: clientDataRes.data.client,
    lastAppointment: clientDataRes.data.lastAppointments.data[0],
    appointments: appointmentsRes.data?.appointments?.data || [],
    nextAppointmentNotes,
    reservationSettings:
      reservationSettings.data?.locationSettings?.data?.reservation_settings ??
      {},
  };
}

function NextAppointmentNoteDialog({ close, client_id }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const { loading, onSubmit, errors } = useFormAction(
    (e) => api.createNextAppointmentNote({ input: formData(e.target) }),
    () => {
      revalidator.revalidate();
      close();
    },
  );

  return (
    <Dialog open onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("client.noteForNextAppointment")}
        <IconButton aria-label="close" onClick={close}>
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form id="NextAppointmentNoteDialogForm" onSubmit={onSubmit}>
          <input type="hidden" name="client_id" value={client_id} />
          <input type="hidden" name="role" value={role.Floor_Manager} />
          <TextField
            label={t("client.writeNote")}
            name="note"
            fullWidth
            multiline
            minRows={4}
            required
          />
          <FormErrorList formErrors={errors} />
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={close}>
          {t("form.cancel")}
        </Button>
        <Button
          variant="contained"
          type="submit"
          form="NextAppointmentNoteDialogForm"
          disabled={loading}
        >
          {loading && <ButtonLoader />}
          {t("buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ClientContent({ displayMinimalInfo = false }) {
  const { t } = useTranslation();
  const loaderData = useLoaderData();
  const {
    client,
    appointments,
    nextAppointmentNotes,
    nameDayNames,
    lastAppointment,
    reservationSettings,
  } = loaderData;

  const [tabValue, setTabValue] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") || "1";
  });
  const [adhocDialogOpen, setAdhocDialogOpen] = useState(false);
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [
    noteForNextAppointmentDialogOpen,
    setNoteForNextAppointmentDialogOpen,
  ] = useState(false);
  const [cancelAppointment, setCancelAppointment] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const rescheduleId = params.get("reschedule");
  const deleteId = params.get("delete");
  useEffect(() => {
    const urlObj = new URL(window.location.href);
    if (rescheduleId) {
      handleOpenReservationDialog({
        appointment: appointments.find((a) => a.id === rescheduleId),
        client,
        mode: RESERVATION_MODES.RESCHEDULE,
        order_from_h: reservationSettings.order_from_h,
      });
      urlObj.searchParams.delete("reschedule");
      window.history.replaceState({}, "", urlObj.toString());
    } else if (deleteId) {
      const appointmentToDelete = appointments.find((a) => a.id === deleteId);
      setCancelAppointment(appointmentToDelete);
      urlObj.searchParams.delete("delete");
      window.history.replaceState({}, "", urlObj.toString());
    }
  }, [rescheduleId, deleteId]);

  const roles = useRoles();
  const canSeeAdhocAndReservationButtons = [
    role.Back_Office,
    role.Front_Office,
    role.Manager_Customer_Care,
  ].some((r) => roles.has(r));

  const canSeeEditButton = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
    ]),
  );
  const canSetNoteForNextAppointment = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Treatment_Coordinator,
      role.Doctor,
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
    ]),
  );
  const canSeeNextAppointmentNoteCard = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Back_Office,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );

  const canSeeAppointmentHistoryTab = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Treatment_Coordinator,
      role.Doctor,
      role.Back_Office,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );

  const canSeeContactsCard = isGrantedAny(
    roles,
    new Set([
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );

  const canSeeProblemCard = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Treatment_Coordinator,
      role.Doctor,
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );
  const canSeePaymentCard = isGrantedAny(
    roles,
    new Set([
      role.Treatment_Coordinator,
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );

  const canSeeHistoryChangesTab = isGrantedAny(
    roles,
    new Set([
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );

  const handleTabChange = useCallback((_, newValue) => {
    setTabValue(newValue);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", newValue);
    window.history.replaceState({}, "", url.toString());
  }, []);

  const handleOpenAdhocDialog = () => {
    setAdhocDialogOpen({
      close: handleCloseAdhocDialog,
      defaults: {
        appointment: appointments[0] ?? null,
        client: client,
        types: [
          appointmentAdhocType.STREET,
          appointmentAdhocType.INITIAL_EXAMINATION,
        ],
      },
    });
  };
  const handleCloseAdhocDialog = () => setAdhocDialogOpen(false);

  const handleOpenReservationDialog = (defaults) => {
    setReservationDialogOpen({
      close: handleCloseReservationDialog,
      defaults: defaults || {
        client,
        mode: RESERVATION_MODES.CREATE,
        order_from_h: reservationSettings.order_from_h,
      },
    });
  };
  const handleCloseReservationDialog = () => setReservationDialogOpen(false);

  if (!client) return <div>{t("client.notFound")}</div>;

  function pickLatestReceptionVersion(notes) {
    if (!Array.isArray(notes)) return null;
    const rec = notes.find((n) => n?.note_type === "receptionist");
    if (!rec?.receptionist_versions?.length) return null;

    const explicitLatest = rec.receptionist_versions.find((v) => v.is_latest);
    if (explicitLatest) return explicitLatest;

    const byVersionNum = rec.receptionist_versions.reduce((a, b) => {
      const av = a?.version_number ?? -Infinity;
      const bv = b?.version_number ?? -Infinity;
      return bv > av ? b : a;
    }, null);
    if (byVersionNum) return byVersionNum;

    return rec.receptionist_versions.reduce((a, b) => {
      const at = a?.edited_at ? Date.parse(a.edited_at) : -Infinity;
      const bt = b?.edited_at ? Date.parse(b.edited_at) : -Infinity;
      return bt > at ? b : a;
    }, null);
  }

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: "var(--app-sticky-top)",
          zIndex: 10,
          backgroundColor: "background.default",
          gap: 2,
        }}
      >
        <ClientAvatar
          client={client}
          t={t}
          nameDayNames={nameDayNames}
          displayMinimalInfo={displayMinimalInfo}
          canSeeClientProblem={canSeeProblemCard}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            borderBottom: 1,
            borderColor: "divider",
            gap: 2,
          }}
        >
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={t("client.tabs.overview")} value="1" />
            {canSeeAppointmentHistoryTab && (
              <Tab label={t("client.tabs.appointmentHistory")} value="2" />
            )}
            <Tab label={t("client.tabs.documents")} value="3" />
            <Tab label={t("client.tabs.gallery")} value="4" />
            {canSeeHistoryChangesTab && <Tab label="Historie změn" value="5" />}
          </Tabs>

          <Box sx={{ display: "flex", gap: 2 }}>
            {canSeeEditButton && (
              <Button variant="outlined" onClick={() => setEditDialog(true)}>
                Úprava karty
              </Button>
            )}
            {canSetNoteForNextAppointment && (
              <Button
                variant="outlined"
                onClick={() => setNoteForNextAppointmentDialogOpen(true)}
              >
                {t("client.noteForNextAppointment")}
              </Button>
            )}
            {canSeeAdhocAndReservationButtons && (
              <>
                <Button
                  variant="outlined"
                  onClick={handleOpenAdhocDialog}
                  startIcon={<FontAwesomeIcon icon={faCalendarDays} />}
                  disabled={!canBeTreated(client)}
                >
                  {t("reception.adhoc")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={async () => {
                    const { data } = await api.fetchClientNotes(client.id);

                    const lastReceptionNote = pickLatestReceptionVersion(
                      data.clientNotes,
                    );

                    handleOpenReservationDialog({
                      client,
                      mode: RESERVATION_MODES.CREATE,
                      order_from_h: reservationSettings.order_from_h,
                      note: lastReceptionNote || null,
                    });
                  }}
                  startIcon={<FontAwesomeIcon icon={faCalendarDays} />}
                >
                  {t("reception.reservation")}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <TabContext value={tabValue}>
        <TabPanel value="1">
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={5}>
              <ClientInfoCard
                client={client}
                lastAppointment={lastAppointment}
              />
            </Grid>
            {canSeePaymentCard && (
              <Grid size={3}>
                <PaymentCard client={client} />
              </Grid>
            )}
            {canSeeContactsCard && (
              <Grid size={4}>
                <ContactsCard client={client} />
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2}>
            <Grid size={3}>
              <NextAppointmentCard
                appointments={appointments}
                client={client}
                handleOpenReservationDialog={handleOpenReservationDialog}
                cancelAppointment={cancelAppointment}
                setCancelAppointment={setCancelAppointment}
                displayMinimalInfo={displayMinimalInfo}
              />
            </Grid>
            {!displayMinimalInfo && canSeeNextAppointmentNoteCard && (
              <Grid size={3}>
                <NextAppointmentNoteCard notes={nextAppointmentNotes} />
              </Grid>
            )}
            <Grid size={3}>
              <RiskAllergyCard client={client} />
            </Grid>
            {!displayMinimalInfo && canSeeProblemCard && (
              <Grid size={3}>
                <PrefCard client={client} />
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography
                variant="h2"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  my: 2,
                }}
              >
                Produkty
              </Typography>
              <TreatmentCard client={client} />
            </Grid>
          </Grid>
        </TabPanel>

        {canSeeAppointmentHistoryTab && (
          <TabPanel value="2" sx={{ position: "relative" }}>
            <AppointmentHistoryCard clientId={client.id} />
          </TabPanel>
        )}

        <TabPanel value="3">
          <Documents client_id={client.id} />
        </TabPanel>

        {tabValue === "4" && (
          <TabPanel value="4">
            <Gallery client_id={client.id} />
          </TabPanel>
        )}

        <TabPanel value="5">
          <Typography variant="h1" sx={{ mt: 2, mb: 3 }}>
            Historie změn
          </Typography>
          <History clientId={client.id} />
        </TabPanel>
      </TabContext>

      {adhocDialogOpen && (
        <NewAdhocDialog
          close={adhocDialogOpen.close}
          defaults={adhocDialogOpen.defaults}
        />
      )}

      {reservationDialogOpen && (
        <ReservationDialog
          close={reservationDialogOpen.close}
          defaults={reservationDialogOpen.defaults}
        />
      )}

      {noteForNextAppointmentDialogOpen && (
        <NextAppointmentNoteDialog
          client_id={client.id}
          close={() => setNoteForNextAppointmentDialogOpen(false)}
        />
      )}
      {editDialog && (
        <EditDialog client_id={client.id} close={() => setEditDialog(false)} />
      )}
    </>
  );
}

export default ClientContent;
